// ============================================================================
// API Service - Backend Communication Layer
// ============================================================================

import type {
  EnrichedTranchesResponse,
  PositionDetail,
  PositionsResponse,
  JournalData,
  Portfolio,
  ApiResponse,
} from '../types';
import { Broker } from '../types';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Generic fetch wrapper with error handling
 */
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      credentials: 'include',  // Include cookies for cross-origin requests
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('API Error:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Network error occurred',
      },
    };
  }
}

// ============================================================================
// Portfolio APIs
// ============================================================================

/**
 * Get all portfolios for the current user
 */
export async function getPortfolios(): Promise<ApiResponse<Portfolio[]>> {
  return fetchApi<Portfolio[]>('/api/portfolios/');
}

/**
 * Get a single portfolio by ID
 */
export async function getPortfolio(portfolioId: string): Promise<ApiResponse<Portfolio>> {
  return fetchApi<Portfolio>(`/api/portfolios/${portfolioId}/`);
}

/**
 * Create a new portfolio
 */
export async function createPortfolio(data: {
  name: string;
  totalSize: number;
  categories: Array<{
    name: string;
    percentage: number;
    timeframe: string;
    color: string;
  }>;
  brokers: string[];
}): Promise<ApiResponse<Portfolio>> {
  return fetchApi<Portfolio>('/api/portfolios/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing portfolio
 */
export async function updatePortfolio(
  portfolioId: string,
  data: {
    name?: string;
    totalSize?: number;
    categories?: Array<{
      name: string;
      percentage: number;
      timeframe: string;
      color: string;
    }>;
    brokers?: string[];
  }
): Promise<ApiResponse<Portfolio>> {
  return fetchApi<Portfolio>(`/api/portfolios/${portfolioId}/`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a portfolio
 */
export async function deletePortfolio(portfolioId: string): Promise<ApiResponse<null>> {
  return fetchApi<null>(`/api/portfolios/${portfolioId}/`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Position APIs
// ============================================================================

/**
 * Get all positions for a portfolio
 */
export async function getPositions(portfolioId: string): Promise<ApiResponse<PositionsResponse>> {
  return fetchApi<PositionsResponse>(`/api/portfolios/${portfolioId}/positions/`);
}

/**
 * Get journal data (closed positions + summary) for a portfolio
 */
export async function getJournalData(portfolioId: string): Promise<ApiResponse<JournalData>> {
  return fetchApi<JournalData>(`/api/portfolios/${portfolioId}/journal/`);
}

/**
 * Get stock detail for a specific position
 */
export async function getStockDetail(
  portfolioId: string,
  stockId: string
): Promise<ApiResponse<PositionDetail>> {
  return fetchApi<PositionDetail>(`/api/portfolios/${portfolioId}/positions/stocks/${stockId}/`);
}

/**
 * Get enriched tranches with FIFO matching and per-tranche metrics
 */
export async function getEnrichedTranches(
  portfolioId: string,
  stockId: string
): Promise<ApiResponse<EnrichedTranchesResponse>> {
  return fetchApi<EnrichedTranchesResponse>(
    `/api/portfolios/${portfolioId}/positions/stocks/${stockId}/tranches/`
  );
}

// ============================================================================
// Notes APIs
// ============================================================================

/**
 * Add a note to a stock
 */
export async function addNote(
  portfolioId: string,
  stockId: string,
  content: string
): Promise<ApiResponse<{ id: string }>> {
  return fetchApi<{ id: string }>(
    `/api/portfolios/${portfolioId}/positions/stocks/${stockId}/notes/`,
    {
      method: 'POST',
      body: JSON.stringify({ content }),
    }
  );
}

/**
 * Update a note
 */
export async function updateNote(
  portfolioId: string,
  stockId: string,
  noteId: string,
  content: string
): Promise<ApiResponse<{ id: string }>> {
  return fetchApi<{ id: string }>(
    `/api/portfolios/${portfolioId}/positions/stocks/${stockId}/notes/${noteId}/`,
    {
      method: 'PUT',
      body: JSON.stringify({ content }),
    }
  );
}

/**
 * Delete a note
 */
export async function deleteNote(
  portfolioId: string,
  stockId: string,
  noteId: string
): Promise<ApiResponse<void>> {
  return fetchApi<void>(
    `/api/portfolios/${portfolioId}/positions/stocks/${stockId}/notes/${noteId}/`,
    {
      method: 'DELETE',
    }
  );
}

/**
 * Update stoploss for a position
 */
export async function updateStoploss(
  portfolioId: string,
  stockId: string,
  stoploss: number
): Promise<ApiResponse<any>> {
  return fetchApi(`/api/portfolios/${portfolioId}/positions/stocks/${stockId}/stoploss/`, {
    method: 'PATCH',
    body: JSON.stringify({ stoploss }),
  });
}

// ============================================================================
// Position Creation & Tranche APIs
// ============================================================================

/**
 * Resolve ISINs and BSE scrip codes to security info
 * Used during import to validate and enrich trade data
 */
export async function resolveSecurities(
  isins: string[],
  scripCodes: string[]
): Promise<ApiResponse<{
  resolved: Array<{
    isin: string;
    ticker: string;
    exchange: 'NSE' | 'BSE';
    bse_scrip_code?: string;
    name?: string;
  }>;
  unresolved: {
    isins: string[];
    scripCodes: string[];
  };
}>> {
  return fetchApi('/api/stock-prices/resolve/', {
    method: 'POST',
    body: JSON.stringify({ isins, scripCodes }),
  });
}

/**
 * Resolve broker-specific symbols to ISINs via broker_symbol_mappings
 * Used for brokers that don't include ISIN in their tradebooks (e.g., ICICI Direct)
 */
export async function resolveBrokerSymbols(
  broker: string,
  lookups: Array<{ value: string; exchange: string }>,
  matchField: Record<string, string>
): Promise<ApiResponse<{
  resolved: Record<string, string>;  // key (e.g., "NSE:RELIANCE") -> ISIN
  unresolved: string[];
}>> {
  return fetchApi('/api/broker-symbols/resolve/', {
    method: 'POST',
    body: JSON.stringify({ broker, lookups, matchField }),
  });
}

export async function createPosition(
  portfolioId: string,
  data: {
    isin: string;                // MANDATORY - canonical identifier
    ticker: string;
    name: string;
    category: string;
    broker: Broker;
    exchange: {
      name: 'NSE' | 'BSE';
      bse_scrip_code?: string;
    };
  }
): Promise<ApiResponse<any>> {
  return fetchApi(`/api/portfolios/${portfolioId}/positions/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function addTranche(
  portfolioId: string,
  positionId: string,
  data: {
    orderId: string;            // Required by backend API
    type: 'buy' | 'sell';
    quantity: number;
    price: number;
    broker: Broker;
    timestamp: string;
    stoploss?: number;
    note?: string;
  }
): Promise<ApiResponse<any>> {
  return fetchApi(`/api/portfolios/${portfolioId}/positions/${positionId}/tranches/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Check for duplicate orderIds in existing positions (both open and closed)
 * Used during import to prevent re-importing the same trades
 */
export interface DuplicateCheckResult {
  isin: string;
  orderId: string;
  isAlreadyImported: boolean;
}

export async function checkDuplicateOrderIds(
  portfolioId: string,
  trades: Array<{ isin: string; orderIds: string[] }>
): Promise<ApiResponse<DuplicateCheckResult[]>> {
  return fetchApi(`/api/portfolios/${portfolioId}/positions/check-duplicates/`, {
    method: 'POST',
    body: JSON.stringify({ trades }),
  });
}

// ============================================================================
// Position & Tranche Deletion APIs
// ============================================================================

/**
 * Delete a position (and all its tranches) permanently
 */
export async function deletePosition(
  portfolioId: string,
  positionId: string
): Promise<ApiResponse<null>> {
  return fetchApi<null>(
    `/api/portfolios/${portfolioId}/positions/stocks/${positionId}/`,
    {
      method: 'DELETE',
    }
  );
}

/**
 * Response from delete tranches operation
 */
export interface DeleteTranchesResult {
  deletedTrancheIds: string[];
  deletedTimelineEventIds: string[];
  deletedStoplossHistoryIds: string[];
  remainingTranches: number;
}

/**
 * Delete specific tranches from a position
 * Note: Cannot delete all tranches - at least one must remain
 */
export async function deleteTranches(
  portfolioId: string,
  positionId: string,
  trancheIds: string[]
): Promise<ApiResponse<DeleteTranchesResult>> {
  return fetchApi<DeleteTranchesResult>(
    `/api/portfolios/${portfolioId}/positions/stocks/${positionId}/tranches/`,
    {
      method: 'DELETE',
      body: JSON.stringify({ trancheIds }),
    }
  );
}

export default {
  getPortfolios,
  getPortfolio,
  createPortfolio,
  updatePortfolio,
  deletePortfolio,
  getPositions,
  getJournalData,
  getStockDetail,
  getEnrichedTranches,
  updateStoploss,
  addNote,
  updateNote,
  deleteNote,
  resolveSecurities,
  createPosition,
  addTranche,
  checkDuplicateOrderIds,
  deletePosition,
  deleteTranches,
};
